import React, { createContext, useContext, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { SerializedError } from "@reduxjs/toolkit"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { loginUserSchema, registerUserSchema } from "validations/auth"
import { PENDING } from "constants/index"
import { WRONG_CREDENTIALS } from "constants/errors"
import { NAVIGATE_TO_SPECIFIC_PATH, NAVIGATE_TO_LOGIN_PAGE, NAVIGATE_TO_FEED_PAGE } from "constants/events"
import { useAppSelector } from "appRedux/hooks"
import { useLoginMutation, useLogoutMutation, useRegisterMutation } from "appRedux/slices/authApiSlice"
import { updateUserIsAuthenticated } from "appRedux/slices/userSlice"
import { useLazyInitQuery } from "appRedux/slices/initApiSlice"

interface AuthContextType {
  user: Profile | null;
  register: (credentials: registerRequestPayload) => Promise<void>;
  login: (credentials: loginRequestPayload) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isUserRegisterLoading: boolean;
  isUserLoginLoading: boolean;
  isUserLogoutLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isUserRegisterLoading, setIsUserRegisterLoading] = useState<boolean>(false)
  const [isUserLoginLoading, setIsUserLoginLoading] = useState<boolean>(false)
  const [isUserLogoutLoading, setIsUserLogoutLoading] = useState<boolean>(false)

  const user = useAppSelector((state) => state.user.profile)
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated)

  const [registerUser, registerUserResult] = useRegisterMutation()
  const [loginUser, loginUserResult] = useLoginMutation()
  const [logoutUser, logoutUserResult] = useLogoutMutation()
  const [triggerInitQuery] = useLazyInitQuery()

  const dispatch = useDispatch()

  useEffect(() => {
    if (registerUserResult.status === "fulfilled") {
      handleRegisteredSuccessfully()
    } else if (registerUserResult.status === "rejected") {
      handleRegisterFailed()
    } else if (registerUserResult.status === PENDING) {
      handleIsRegistering()
    }
  }, [registerUserResult])

  useEffect(() => {
    if (loginUserResult.status === "fulfilled") {
      handleLoggedInSuccessfully()
    } else if (loginUserResult.status === "rejected") {
      handleLoginFailed()
    } else if (loginUserResult.status === PENDING) {
      handleIsLoggingIn()
    }
  }, [loginUserResult])

  useEffect(() => {
    if (logoutUserResult.status === "fulfilled") {
      handleLoggedOutSuccessfully()
    } else if (loginUserResult.status === "rejected") {
      handleLogoutFailed()
    } else if (loginUserResult.status === PENDING) {
      handleIsLoggingOut()
    }
  }, [logoutUserResult])

  function handleRegisteredSuccessfully() {
    dispatch(updateUserIsAuthenticated(true))
    setIsUserRegisterLoading(false)
    triggerInitQuery()
  }

  function handleRegisterFailed() {
    dispatch(updateUserIsAuthenticated(false))
    setIsUserRegisterLoading(false)
  }

  function handleIsRegistering() {
    dispatch(updateUserIsAuthenticated(false))
    setIsUserRegisterLoading(true)
  }

  function handleLoggedInSuccessfully() {
    dispatch(updateUserIsAuthenticated(true))
    setIsUserLoginLoading(false)
    const qs = window.location.search
    const referer = extractReferer(qs)
    if (referer) {
      document.dispatchEvent(new CustomEvent(NAVIGATE_TO_SPECIFIC_PATH, { detail: { targetPath: referer } }))
    } else {
      document.dispatchEvent(new CustomEvent(NAVIGATE_TO_FEED_PAGE))
    }
    triggerInitQuery()
  }

  function handleLoginFailed() {
    dispatch(updateUserIsAuthenticated(false))
    // @ts-ignore
    handleUnauthenticatedUserError(loginUserResult.error)
    setIsUserLoginLoading(false)
  }

  function handleIsLoggingIn() {
    dispatch(updateUserIsAuthenticated(false))
    setIsUserLoginLoading(true)
  }

  function handleLoggedOutSuccessfully() {
    setIsUserLogoutLoading(false)
    dispatch(updateUserIsAuthenticated(false))
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_LOGIN_PAGE))
  }

  function handleLogoutFailed() {
    setIsUserLogoutLoading(false)
  }

  function handleIsLoggingOut() {
    setIsUserLogoutLoading(true)
  }

  async function register(credentials: registerRequestPayload) {
    try {
      const { email, username, password, confirmPassword } = await registerUserSchema.parseAsync(credentials)
      registerUser({ email, username, password, confirmPassword })
    } catch (error) {
      console.error(`Error happened in user register. ${error}`)
    }
  }

  async function login(credentials: loginRequestPayload) {
    try {
      const { username, password } = await loginUserSchema.parseAsync(credentials)
      loginUser({ username, password })
    } catch (error) {
      console.error(`Error happened in user login. ${error}`)
      handleUnauthenticatedUserError(null)
    }
  }

  async function logout() {
    try {
      logoutUser()
    } catch (error) {
      console.error(`Error happened in user logout. ${error}`)
    }
  }

  function handleUnauthenticatedUserError(errorResponse: FetchBaseQueryError | SerializedError | null) {
    let errorMessage = "general.serverError"
    const error: any = getErrorInfo(errorResponse)
    if (error && error.hasOwnProperty("message") && error.hasOwnProperty("status")) {
      if (error.status === 401) {
        errorMessage = `errorMessage.${WRONG_CREDENTIALS}`
        document.dispatchEvent(new CustomEvent(NAVIGATE_TO_LOGIN_PAGE))
      }
    }
    document.dispatchEvent(new CustomEvent("showUnauthenticatedUserError", {
      detail: {
        status: error?.status,
        message: errorMessage
      }
    }))
  }

  function getErrorInfo(error: FetchBaseQueryError | SerializedError | null) {
    if (error && "status" in error && "data" in error) {
      return error.data !== null ? error.data : { status: error.status, message: "" }
    } else if (error && "code" in error && "message" in error) {
      return { status: error.code, message: error.message }
    } else {
      return null
    }
  }

  function extractReferer(queryString: string) {
    const match = queryString.match(/(?<=\breferer=)(\/[^&]*)/)
    return match ? match[0] : null
  }

  return (
    <AuthContext.Provider
      value={{ user, register, login, logout, isAuthenticated, isUserRegisterLoading, isUserLoginLoading, isUserLogoutLoading }}>
      {children}
    </AuthContext.Provider>
  )
}
